import React, { useEffect, useLayoutEffect, useRef } from "react";
import { Bell, Building, ChevronDown, FlaskConical, Plus, Search, ShoppingCart, User } from "lucide-react";
import { LogoIcone } from "./Logo";
import { useOperandoComo } from "../contexts/OperandoComoContext";
import { useAuth } from "../contexts/AuthContext";

// Header global = A MESMA BARRA DA HOME, em todas as telas (pedido do dono:
// "parecia dois sistemas"). Estrutura idêntica, posição por posição:
//   [voltar] [logo quadrada laranja] [busca branca] [CARRINHO] [SINO]
// Fundo navy #12192a sempre; carrinho ANTES do sino, ícones brancos grandes
// (23px, linha 1.9) — exatamente como no topo da home. A busca aqui é um
// atalho (toca → tela de busca). Mesmas rotas de sempre; nada removido.
export const Header = ({ tela, nav, cart, notifs }) => {
  const nLidas = (notifs || []).filter(n => !n.lida).length;
  const op = useOperandoComo();
  const { user } = useAuth();
  const ehTestUser = !!user?.is_test_user;
  // Sem seta de voltar (pedido do dono): a barra fica IDÊNTICA em toda tela —
  // logo, busca, carrinho e sino sempre nas MESMAS posições. Voltar fica por
  // conta dos botões "Voltar" internos das telas / gesto do celular.

  // Altura REAL do header publicada em --header-h. Telas com barra própria
  // grudada abaixo dele (ex.: as abas de Locações) usavam 66px chumbado, que
  // só vale sem banner: com "MODO TESTE" ou "Operando como" o header passa de
  // 90px e cobria a barra de abas ao rolar (header é z-40).
  const refHeader = useRef(null);
  useLayoutEffect(() => {
    const el = refHeader.current;
    if (!el || typeof window === 'undefined') return undefined;
    const publicar = () => {
      const h = Math.round(el.getBoundingClientRect().height);
      if (h > 0) document.documentElement.style.setProperty('--header-h', `${h}px`);
    };
    publicar();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(publicar);
      ro.observe(el);
    }
    window.addEventListener('resize', publicar);
    return () => { if (ro) ro.disconnect(); window.removeEventListener('resize', publicar); };
    // Sem deps: o ResizeObserver já cobre o header crescendo/encolhendo quando
    // um banner entra ou sai. Recriar o observer a cada render seria desperdício.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => () => {
    if (typeof document !== 'undefined') document.documentElement.style.removeProperty('--header-h');
  }, []);

  return (
    // pt = safe-area-inset-top: instalado como APP (PWA em tela cheia), o
    // relógio/bateria do celular ficam POR CIMA do cabeçalho e cobriam a busca
    // (relato do dono, 05/08). No navegador o inset é 0 e nada muda.
    // A altura publicada em --header-h já inclui esse padding, então a barra de
    // abas de Locações continua grudando no lugar certo.
    <header
      ref={refHeader}
      className="sticky top-0 z-40 bg-[#12192a]"
      style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
    >
      {/* Banner persistente: MODO TESTE (sandbox por login) */}
      {ehTestUser && (
        <div className="bg-yellow-400 text-yellow-950 px-4 py-1 flex items-center justify-center gap-2 text-[11px] font-bold">
          <FlaskConical size={12} />
          <span>MODO TESTE — dados isolados de produção</span>
        </div>
      )}

      {/* Banner persistente: usuário operando como empresa */}
      {op?.estaComoPJ && (
        <div className="bg-violet-600 text-white px-4 py-1 flex items-center justify-center gap-2 text-[11px] font-bold">
          {op.empresaAtual?.logo_url
            ? <img src={op.empresaAtual.logo_url} alt="Logo" className="w-4 h-4 rounded object-cover" />
            : <Building size={11} />}
          <span>Operando como <strong>{op.empresaAtual?.razao_social || op.empresaAtual?.nome_fantasia}</strong></span>
          <button onClick={() => nav('perfil')} className="ml-2 underline opacity-90 hover:opacity-100">Trocar</button>
        </div>
      )}

      {/* Barra idêntica ao topo da home — no MOBILE (logo + busca + carrinho
          + sino) e no DESKTOP (wordmark, botão Buscar, + Anunciar/Locações,
          sino → carrinho → avatar), pra ser UM sistema só em toda tela. */}
      <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-2.5">
        <button onClick={() => nav('home')} className="flex-shrink-0 flex items-center"
          style={{ filter: 'drop-shadow(0 4px 10px rgba(234,88,12,.45))' }} aria-label="Tô Alugando">
          <LogoIcone size={38} />
        </button>
        <button onClick={() => nav('home')}
          className="hidden md:block text-white font-extrabold text-lg tracking-tight flex-shrink-0 mr-2 text-left" style={{ letterSpacing: '-0.02em' }}>
          Tô <span className="text-orange-500">Alugando</span>
        </button>

        {/* Busca (atalho — mesma cara do campo da home, com Buscar no desktop) */}
        <button onClick={() => nav('busca')}
          className="flex-1 flex items-center gap-2 bg-white rounded-xl px-3 md:pr-[5px] h-[42px] border border-[#F1D9C2] shadow-sm min-w-0 text-left md:max-w-2xl md:mx-auto">
          <Search size={18} className="text-slate-400 flex-shrink-0" strokeWidth={2.1} />
          <span className="flex-1 text-sm text-slate-400 font-medium truncate">O que você precisa?</span>
          {/* Pill concêntrica com 5px de folga (retângulo colado ficava estranho) */}
          <span className="hidden md:flex items-center justify-center h-[32px] px-[18px] rounded-[9px] bg-orange-600 text-white text-[13px] font-bold flex-shrink-0">Buscar</span>
        </button>

        {/* Atalhos ao lado do Buscar — botões de verdade (não texto solto) */}
        <div className="hidden md:flex items-center gap-2.5 flex-shrink-0 ml-1 mr-3">
          <button onClick={() => nav('publicar')}
            className="inline-flex items-center gap-1.5 bg-orange-600 hover:bg-orange-700 text-white text-[13px] font-bold pl-3 pr-4 py-2 rounded-lg transition-colors">
            <Plus size={15} strokeWidth={2.6} /> Anunciar
          </button>
          <button onClick={() => nav('locacoes_central')}
            className="inline-flex items-center text-[13px] font-semibold text-white border border-white/25 hover:border-white/50 hover:bg-white/5 px-4 py-2 rounded-lg transition-colors">
            Locações
          </button>
        </div>

        {/* Mobile: carrinho → sino (como sempre). Desktop: sino → carrinho →
            avatar, na MESMA ordem da home (md:order-*). */}
        <button onClick={() => nav('carrinho')} className="relative p-1 flex-shrink-0 md:order-2" aria-label="Carrinho">
          <ShoppingCart size={23} className="text-white" strokeWidth={1.9} />
          {(cart || []).length > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 bg-orange-500 text-white text-[9.5px] font-bold rounded-full flex items-center justify-center">{cart.length}</span>
          )}
        </button>
        <button onClick={() => nav('notificacoes')} className="relative p-1 flex-shrink-0 md:order-1" aria-label="Notificações">
          <Bell size={23} className="text-white" strokeWidth={1.9} />
          {nLidas > 0 && (
            <span className="absolute -top-1 -right-1 min-w-[15px] h-[15px] px-1 bg-red-500 text-white text-[9.5px] font-bold rounded-full flex items-center justify-center">{nLidas}</span>
          )}
        </button>
        {/* Perfil (avatar + seta) — só desktop, igual à home */}
        <button onClick={() => nav('perfil')} className="hidden md:flex items-center gap-1 flex-shrink-0 md:order-3 ml-1" aria-label="Perfil">
          <span className="w-8 h-8 rounded-full bg-white/15 border border-white/20 flex items-center justify-center text-white text-[13px] font-bold uppercase">
            {user?.nome ? user.nome[0] : <User size={15} className="text-white" strokeWidth={2} />}
          </span>
          <ChevronDown size={14} className="text-white/60" strokeWidth={2.4} />
        </button>
      </div>
    </header>
  );
};
